import * as React from "react";

type TransitionProps = {
  /**
   * When false, plays the "leave" transition (via `data-closed`)
   * and unmounts after the CSS transition completes.
   *
   * If omitted, defaults to `true` (useful for keyed "appear" transitions).
   */
  readonly show?: boolean;
  /**
   * Kept for API compatibility with Headless UI.
   * This implementation always "appears" on mount to preserve existing usage.
   */
  readonly appear?: boolean;
  readonly children: React.ReactElement;
};

function toMs(value: string): number {
  const v = value.trim();
  if (!v) return 0;
  if (v.endsWith("ms")) return Number.parseFloat(v);
  if (v.endsWith("s")) return Number.parseFloat(v) * 1000;
  return Number.parseFloat(v);
}

function maxTotalMs(durations: string, delays: string): number {
  const ds = durations.split(",").map(toMs);
  const ls = delays.split(",").map(toMs);
  const count = Math.max(ds.length, ls.length);
  let max = 0;
  for (let i = 0; i < count; i += 1) {
    const d = ds[i % ds.length] ?? 0;
    const l = ls[i % ls.length] ?? 0;
    max = Math.max(max, d + l);
  }
  return max;
}

function getMaxMotionMs(el: HTMLElement): number {
  const style = globalThis.getComputedStyle(el);

  const transitionMs = maxTotalMs(style.transitionDuration, style.transitionDelay);
  const animationMs = maxTotalMs(style.animationDuration, style.animationDelay);

  return Math.max(transitionMs, animationMs);
}

function mergeRefs<T>(...refs: Array<React.Ref<T> | undefined>) {
  return (value: T) => {
    for (const ref of refs) {
      if (!ref) continue;
      if (typeof ref === "function") {
        ref(value);
      } else {
        // eslint-disable-next-line no-param-reassign
        (ref as React.MutableRefObject<T | null>).current = value;
      }
    }
  };
}

export const Transition = ({ show = true, children }: TransitionProps) => {
  const localRef = React.useRef<HTMLElement | null>(null);

  const [present, setPresent] = React.useState(show);
  const [closed, setClosed] = React.useState(true);

  const closeTimeoutRef = React.useRef<number | null>(null);
  const rafRef = React.useRef<number | null>(null);

  const clearTimers = React.useCallback(() => {
    if (closeTimeoutRef.current != null) {
      globalThis.clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    if (rafRef.current != null) {
      globalThis.cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
  }, []);

  React.useEffect(() => {
    clearTimers();

    if (show) {
      setPresent(true);
      // Start "closed" for a frame, then open to trigger Tailwind `data-[closed]` transitions.
      setClosed(true);
      rafRef.current = globalThis.requestAnimationFrame(() => {
        setClosed(false);
      });
      return;
    }

    // Closing: mark as closed, then unmount once transitions complete.
    setClosed(true);
    rafRef.current = globalThis.requestAnimationFrame(() => {
      const el = localRef.current;
      const ms = el ? getMaxMotionMs(el) : 0;
      closeTimeoutRef.current = globalThis.setTimeout(() => {
        setPresent(false);
      }, ms + 50) as unknown as number;
    });
  }, [clearTimers, show]);

  React.useEffect(() => clearTimers, [clearTimers]);

  if (!present) return null;

  const childProps = children.props as Record<string, unknown> & {
    onTransitionEnd?: React.TransitionEventHandler;
    onAnimationEnd?: React.AnimationEventHandler;
  };

  return React.cloneElement(children as React.ReactElement<Record<string, unknown>>, {
    // Tailwind variant expects `data-closed` attribute to exist when closed.
    "data-closed": closed ? "" : undefined,
    "onTransitionEnd": (e: React.TransitionEvent) => {
      childProps.onTransitionEnd?.(e);
    },
    "onAnimationEnd": (e: React.AnimationEvent) => {
      childProps.onAnimationEnd?.(e);
    },
    "ref": mergeRefs(
      // If the child is not a DOM element, this ref will remain null, and we’ll fall back to timeout 0.
      (children as unknown as { ref?: React.Ref<HTMLElement> }).ref,
      (node: HTMLElement | null) => {
        localRef.current = node;
      },
    ),
  });
};
